<template>
  <v-container fluid>
    <v-dialog
      v-model="dialog.show"
      persistent
      :overlay="false"
      max-width="600px"
    >
      <v-card>
        <v-card-title class="title secondary mb-4">
          {{ dialog.action }} App Feature
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            These features are read by the Soundpaint app for users who purchased the selected product.
          </p>
          <v-autocomplete
            v-model="dialog.record.ShopifyProductId"
            :items="products"
            item-text="title"
            item-value="id"
            filled
            label="Product"
            :disabled="dialog.action === 'Edit'"
          ></v-autocomplete>
          <v-text-field
            v-model="dialog.record.Feature"
            filled
            label="Feature Value"
            type="text"
            hint="e.g., looper, reverb, delay"
            persistent-hint
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="save()"
            :loading="dialog.saving"
            :disabled="dialog.saving || !dialog.record.ShopifyProductId || !dialog.record.Feature"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="deleteDialog.show"
      persistent
      max-width="400px"
    >
      <v-card>
        <v-card-title class="title secondary mb-4">
          Delete App Feature
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this feature mapping?
          <div class="mt-2">
            <strong>Product:</strong> {{ deleteDialog.productTitle }}<br>
            <strong>Feature:</strong> {{ deleteDialog.feature }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="deleteDialog.show = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            @click="deleteFeature()"
            :loading="deleteDialog.deleting"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row>
      <v-col cols="12" sm="12" class="pb-0">
        <h1 class="headline">
          SOUNDPAINT App Features
        </h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="12" class="pb-0">
        <v-btn
          color="primary"
          class="mb-4"
          @click="openDialog({}, 'Create')"
        >
          Add Feature
        </v-btn>
        <v-data-table
          :headers="headers"
          :items="records"
          :items-per-page="10"
          :loading="loading"
          :search="search"
          sort-by="Feature"
        >
          <template v-slot:top>
            <v-toolbar flat color="white" class="pt-1">
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Filter"
                outlined
                dense
                hide-details
              ></v-text-field>
            </v-toolbar>
          </template>
          <template v-slot:item.ShopifyProductId="{ item }">
            {{ getProductTitle(item.ShopifyProductId) }}
          </template>
          <template v-slot:item.ProductId="{ item }">
            <a :href="`https://${storeUrl}/admin/products/${item.ShopifyProductId}`" target="_blank">
            {{ item.ShopifyProductId }}
            </a>
          </template>
          <template v-slot:item.CreatedAt="{ item }">
            {{ formatDate(item.CreatedAt) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small outlined text class="mr-2" @click="openDialog(item, 'Edit')">
              edit
            </v-btn>
            <v-btn small outlined text color="error" @click="openDeleteDialog(item)">
              delete
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snack = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import fb from "@/main";
import moment from "moment";

export default {
  name: "AppFeatures",
  data() {
    return {
      store: "soundpaint",
      loading: false,
      search: "",
      snack: false,
      snackColor: "",
      snackText: "",
      dialog: {
        action: "",
        saving: false,
        show: false,
        record: {},
      },
      deleteDialog: {
        show: false,
        deleting: false,
        id: null,
        productTitle: "",
        feature: "",
      },
      records: [],
      products: [],
      headers: [
        {
          text: "ID",
          value: "Id",
          align: "left",
          width: 80,
        },
        {
          text: "Product",
          value: "ShopifyProductId",
          align: "left",
        },
        {
          text: "Product ID",
          value: "ProductId",
          align: "left"
        },
        {
          text: "Feature",
          value: "Feature",
          align: "left",
        },
        {
          text: "Created",
          value: "CreatedAt",
          align: "left",
        },
        {
          text: "",
          align: "end",
          value: "actions",
          sortable: false,
          width: 200,
        },
      ],
    };
  },
  computed: {
    storeUrl() {
      return "soundpaint.myshopify.com";
    },
  },
  async created() {
    try {
      this.loading = true;
      await Promise.all([
        this.getAppFeatures(),
        this.getProducts(),
      ]);
      this.loading = false;
    } catch (error) {
      this.loading = false;
      console.error(error);
    }
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format("MMM D, YYYY") : "";
    },
    getProductTitle(productId) {
      const product = this.products.find(p => p.id === productId || p.id === String(productId));
      return product ? product.title : productId;
    },
    async getAppFeatures() {
      const getAppFeaturesCall = fb.functions.httpsCallable("getAppFeatures");
      const { data: recordset } = await getAppFeaturesCall({
        store: this.store,
      });
      if (recordset && recordset.length) {
        this.records = recordset;
      } else {
        this.records = [];
      }
    },
    async getProducts() {
      const getProductsCall = fb.functions.httpsCallable("getProducts");
      const { data: recordset } = await getProductsCall({
        store: this.store,
      });
      if (recordset && recordset.length) {
        this.products = recordset.map(p => ({
          id: String(p.id),
          title: p.title,
        }));
      } else {
        this.products = [];
      }
    },
    openDialog(item, action) {
      this.dialog.action = action;
      if (action === "Create") {
        this.dialog.record = {
          ShopifyProductId: null,
          Feature: "",
        };
      } else {
        this.dialog.record = { ...item };
      }
      this.dialog.show = true;
    },
    openDeleteDialog(item) {
      this.deleteDialog.id = item.Id;
      this.deleteDialog.productTitle = this.getProductTitle(item.ShopifyProductId);
      this.deleteDialog.feature = item.Feature;
      this.deleteDialog.show = true;
    },
    async save() {
      const action = this.dialog.action.toLowerCase();
      try {
        this.dialog.saving = true;
        const saveAppFeatureCall = fb.functions.httpsCallable("saveAppFeature");
        await saveAppFeatureCall({
          store: this.store,
          action: action,
          id: this.dialog.record.Id,
          shopifyProductId: this.dialog.record.ShopifyProductId,
          feature: this.dialog.record.Feature.trim(),
        });

        await this.getAppFeatures();

        this.dialog.show = false;
        this.dialog.saving = false;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = `Successfully ${action}d feature`;
      } catch (error) {
        this.dialog.saving = false;
        this.snack = true;
        this.snackColor = "error";
        this.snackText = `Error ${action}ing feature`;
        console.error(error);
      }
    },
    async deleteFeature() {
      try {
        this.deleteDialog.deleting = true;
        const deleteAppFeatureCall = fb.functions.httpsCallable("deleteAppFeature");
        await deleteAppFeatureCall({
          store: this.store,
          id: this.deleteDialog.id,
        });

        await this.getAppFeatures();

        this.deleteDialog.show = false;
        this.deleteDialog.deleting = false;
        this.snack = true;
        this.snackColor = "success";
        this.snackText = "Successfully deleted feature";
      } catch (error) {
        this.deleteDialog.deleting = false;
        this.snack = true;
        this.snackColor = "error";
        this.snackText = "Error deleting feature";
        console.error(error);
      }
    },
  },
};
</script>
